/**
 * Custom hook to manage mode-specific Redux state for proxy edit panel.
 * Extracts conditional Redux selector logic to reduce cognitive complexity.
 */
import { useAppSelector } from '../../../../store/hooks'
import type { ProxyMode } from '../../../../components/common/config/adminConfig'
import {
  selectAvailableEmployeeResults,
  selectAvailableEmployeeLoading,
  selectAvailableEmployeeSearchPerformed,
  selectAvailableEmployeeError,
  selectAPSaveLoading,
  selectAPSaveError,
  type AvailableEmployeeRecord,
} from '../../../../store/slices/admin/approverProxySlice'
import {
  selectRPAvailableEmployeeResults,
  selectRPAvailableEmployeeLoading,
  selectRPAvailableEmployeeSearchPerformed,
  selectRPAvailableEmployeeError,
  selectRPSaveLoading,
  selectRPSaveError,
} from '../../../../store/slices/admin/requestorProxySlice'
import {
  selectFEAvailableEmployeeResults,
  selectFEAvailableEmployeeLoading,
  selectFEAvailableEmployeeSearchPerformed,
  selectFEAvailableEmployeeError,
  selectFESaveLoading,
  selectFESaveError,
} from '../../../../store/slices/admin/floatEmployeeSlice'

interface ProxyModeState {
  employeeResults: AvailableEmployeeRecord[]
  isLoadingEmployeeSearch: boolean
  employeeSearchPerformed: boolean
  employeeSearchError: string | null
  isLoadingSave: boolean
  saveError: string | null
}

/**
 * Resolves mode-specific Redux state selectors for employee search and save operations.
 * @param mode - Current proxy mode ('approver', 'float', or 'requestor')
 * @returns Redux state for the specified mode
 */
export const useProxyModeState = (mode: ProxyMode): ProxyModeState => {
  // Approver mode selectors
  const apEmployeeResults = useAppSelector(selectAvailableEmployeeResults)
  const apIsLoadingEmployeeSearch = useAppSelector(selectAvailableEmployeeLoading)
  const apEmployeeSearchPerformed = useAppSelector(selectAvailableEmployeeSearchPerformed)
  const apEmployeeSearchError = useAppSelector(selectAvailableEmployeeError)
  const apSaveLoading = useAppSelector(selectAPSaveLoading)
  const apSaveError = useAppSelector(selectAPSaveError)

  // Requestor mode selectors
  const rpEmployeeResults = useAppSelector(selectRPAvailableEmployeeResults)
  const rpIsLoadingEmployeeSearch = useAppSelector(selectRPAvailableEmployeeLoading)
  const rpEmployeeSearchPerformed = useAppSelector(selectRPAvailableEmployeeSearchPerformed)
  const rpEmployeeSearchError = useAppSelector(selectRPAvailableEmployeeError)
  const rpSaveLoading = useAppSelector(selectRPSaveLoading)
  const rpSaveError = useAppSelector(selectRPSaveError)

  // Float mode selectors
  const feEmployeeResults = useAppSelector(selectFEAvailableEmployeeResults)
  const feIsLoadingEmployeeSearch = useAppSelector(selectFEAvailableEmployeeLoading)
  const feEmployeeSearchPerformed = useAppSelector(selectFEAvailableEmployeeSearchPerformed)
  const feEmployeeSearchError = useAppSelector(selectFEAvailableEmployeeError)
  const feSaveLoading = useAppSelector(selectFESaveLoading)
  const feSaveError = useAppSelector(selectFESaveError)

  // Return mode-specific state
  if (mode === 'approver') {
    return {
      employeeResults: apEmployeeResults,
      isLoadingEmployeeSearch: apIsLoadingEmployeeSearch,
      employeeSearchPerformed: apEmployeeSearchPerformed,
      employeeSearchError: apEmployeeSearchError,
      isLoadingSave: apSaveLoading,
      saveError: apSaveError,
    }
  }

  if (mode === 'float') {
    return {
      employeeResults: feEmployeeResults,
      isLoadingEmployeeSearch: feIsLoadingEmployeeSearch,
      employeeSearchPerformed: feEmployeeSearchPerformed,
      employeeSearchError: feEmployeeSearchError,
      isLoadingSave: feSaveLoading,
      saveError: feSaveError,
    }
  }

  // Requestor mode
  return {
    employeeResults: rpEmployeeResults,
    isLoadingEmployeeSearch: rpIsLoadingEmployeeSearch,
    employeeSearchPerformed: rpEmployeeSearchPerformed,
    employeeSearchError: rpEmployeeSearchError,
    isLoadingSave: rpSaveLoading,
    saveError: rpSaveError,
  }
}
